import React from 'react'
import { useParams } from 'react-router-dom'
import { useResetPassword } from '@modmd/data'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import Card from 'components/Card'
import { COLOR, DEVICE, pxToRem } from 'theme'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Logo from 'components/Logo'

interface Props {
  title: string
  newPasswordLabel: string
  newPasswordPlaceholder: string
  newPasswordConfirmationLabel: string
  newPasswordConfirmationPlaceholder: string
  resetPasswordButtonLabel: string
  signInButtonLabel: string
  onSignInButtonClick: () => void
  onSuccess?: () => void
  onFailure?: () => void
}

const ResetPasswordCard: React.VFC<Props> = ({
  title,
  newPasswordLabel,
  newPasswordPlaceholder,
  newPasswordConfirmationLabel,
  newPasswordConfirmationPlaceholder,
  resetPasswordButtonLabel,
  signInButtonLabel,
  onSignInButtonClick,
  onSuccess,
  onFailure,
}) => {
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { token } = useParams<{ token: string }>()
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isResetPasswordLoading,
  } = useResetPassword({
    token,
    onCompleted: () => {
      if (onSuccess) {
        onSuccess()
      }
    },
    onError: () => {
      if (onFailure) {
        onFailure()
      }
    },
  })

  return (
    <Card width="100%" maxWidth={pxToRem(500)}>
      <Card.Title rightContent={<Logo isSmall />}>{title}</Card.Title>
      <Card.Content>
        <Box display="grid" gridGap={pxToRem(15)} mt={isMobile ? pxToRem(150) : undefined}>
          <TextInput
            name="newPassword"
            label={newPasswordLabel}
            type="password"
            placeholder={newPasswordPlaceholder}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.newPassword}
            errorMessage={
              errors.newPassword && touched.newPassword ? errors.newPassword : undefined
            }
            autoComplete="off"
          />
          <TextInput
            name="newPasswordConfirmation"
            label={newPasswordConfirmationLabel}
            type="password"
            placeholder={newPasswordConfirmationPlaceholder}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.newPasswordConfirmation}
            errorMessage={
              errors.newPasswordConfirmation && touched.newPasswordConfirmation
                ? errors.newPasswordConfirmation
                : undefined
            }
            autoComplete="off"
          />
          <Button type="submit" onClick={() => handleSubmit()} disabled={isResetPasswordLoading}>
            {resetPasswordButtonLabel}
          </Button>
          <Box display="flex" justifySelf="center">
            <Button onClick={onSignInButtonClick} appearance="link">
              <Text color={COLOR.brand}>{signInButtonLabel}</Text>
            </Button>
          </Box>
        </Box>
      </Card.Content>
    </Card>
  )
}

export { ResetPasswordCard }
